import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Clock, Users, Star, MapPin, ExternalLink, Quote, Award, Heart, Sparkles, ArrowRight } from 'lucide-react';
import { cn, formatPrice, formatDuration } from '@/lib/utils';
import { useDisplayMode } from '@/hooks/useDisplayMode';
import { useWidgetSettings, usePropertyContext } from '@/context/PropertyContext';
import { api } from '@/lib/api';
import type { Experience } from '@/lib/api';

interface ExperienceCardProps {
  experience: Experience;
  className?: string;
  /** Optional: Display in horizontal layout for list view */
  layout?: 'vertical' | 'horizontal';
  /** Animation delay for staggered loading */
  animationDelay?: number;
}

/**
 * ExperienceCard - Premium luxury card component for displaying experiences
 * Designed for $100M+ hospitality brands
 *
 * Features:
 * - Elegant image hover zoom with smooth easing
 * - Premium gradient overlay for text legibility
 * - Refined category badge with glassmorphism
 * - Animated price reveal on hover
 * - Premium shadows and transitions
 * - Full WCAG 2.1 AA compliance
 */
export function ExperienceCard({
  experience,
  className,
  layout = 'vertical',
  animationDelay = 0,
}: ExperienceCardProps) {
  const displayMode = useDisplayMode();
  const settings = useWidgetSettings();
  const { propertyKey } = usePropertyContext();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  // Determine if this is a curated (external) experience
  const isCurated = experience.type === 'curated';
  const source = experience.source || 'viator';

  // Use coverImage first, then fall back to images array
  const imageUrl = experience.coverImage || experience.images?.[0]?.url;
  
  // Build the link based on display mode (only for first-party experiences)
  const internalLink = displayMode === 'embed'
    ? `/embed/${params.propertyKey || propertyKey}/${experience.slug}`
    : `/${experience.slug}?property=${searchParams.get('property') || propertyKey}`;

  // Format price for screen readers
  const priceText = formatPrice(experience.basePrice, experience.currency);
  const priceLabel = `${priceText} per ${experience.priceType === 'per_group' ? 'group' : 'person'}`;

  // Handle click for curated experiences - track and redirect
  const handleCuratedClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (isRedirecting) return;

    setIsRedirecting(true);
    try {
      const key = params.propertyKey || propertyKey;
      if (!key) {
        throw new Error('Property key not found');
      }
      const result = await api.trackAffiliateClick(key, experience.id);
      const url = result.affiliateUrl || experience.affiliateUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Failed to track affiliate click:', error);
      if (experience.affiliateUrl) {
        window.open(experience.affiliateUrl, '_blank', 'noopener,noreferrer');
      }
    } finally {
      setIsRedirecting(false);
    }
  };

  // Source display name
  const sourceDisplayName = source === 'viator' ? 'Viator' : source;

  // Badge component for staff pick / locally loved
  const BadgeDisplay = () => {
    if (!isCurated) return null;
    
    const badges = [];
    
    if (experience.isStaffPick) {
      badges.push(
        <span 
          key="staff-pick"
          className="inline-flex items-center gap-1 rounded-full bg-amber-100/90 px-2.5 py-1 text-xs font-medium text-amber-800 backdrop-blur-sm"
        >
          <Award className="h-3 w-3" />
          Staff Pick
        </span>
      );
    }
    
    if (experience.isLocallyLoved) {
      badges.push(
        <span 
          key="locally-loved"
          className="inline-flex items-center gap-1 rounded-full bg-rose-100/90 px-2.5 py-1 text-xs font-medium text-rose-800 backdrop-blur-sm"
        >
          <Heart className="h-3 w-3 fill-current" />
          Locally Loved
        </span>
      );
    }
    
    if (experience.customBadge) {
      badges.push(
        <span 
          key="custom"
          className="inline-flex items-center gap-1 rounded-full bg-purple-100/90 px-2.5 py-1 text-xs font-medium text-purple-800 backdrop-blur-sm"
        >
          <Sparkles className="h-3 w-3" />
          {experience.customBadge}
        </span>
      );
    }
    
    if (badges.length === 0) return null;
    
    return <div className="flex flex-wrap gap-1.5 mb-2">{badges}</div>;
  };

  // Concierge note component
  const ConciergeNote = () => {
    if (!isCurated || !experience.conciergeNote) return null;
    
    return (
      <div className="mt-3 rounded-xl bg-primary/5 border border-primary/10 p-3">
        <div className="flex gap-2">
          <Quote className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground italic leading-relaxed">
            {experience.conciergeNote}
          </p>
        </div>
      </div>
    );
  };

  // Card content - shared between layouts
  const cardContent = (isHorizontal: boolean) => (
    <>
      {/* Image Section - Premium Treatment */}
      {settings.showImages && (
        <div className={cn(
          "relative overflow-hidden bg-muted",
          isHorizontal ? "w-52 sm:w-72 flex-shrink-0" : "aspect-[4/3]"
        )}>
          {imageUrl ? (
            <>
              {/* Image with elegant zoom on hover */}
              <img
                src={imageUrl}
                alt=""
                aria-hidden="true"
                onLoad={() => setIsImageLoaded(true)}
                className={cn(
                  "h-full w-full object-cover",
                  "transition-all duration-[800ms] ease-[cubic-bezier(0.22,1,0.36,1)]",
                  "group-hover:scale-[1.08]",
                  !isImageLoaded && "opacity-0"
                )}
                loading="lazy"
              />

              {/* Premium gradient overlay - always visible, darker on hover */}
              <div
                className={cn(
                  "absolute inset-0 transition-opacity duration-700 ease-out",
                  "bg-gradient-to-t from-black/50 via-black/15 to-transparent",
                  "opacity-70 group-hover:opacity-85"
                )}
                aria-hidden="true"
              />

              {/* Subtle vignette */}
              <div 
                className="absolute inset-0 image-overlay-vignette opacity-50" 
                aria-hidden="true"
              />

              {/* Image skeleton while loading */}
              {!isImageLoaded && (
                <div className="absolute inset-0 skeleton-shine" />
              )}
            </>
          ) : (
            <div className="flex h-full items-center justify-center bg-gradient-to-br from-muted to-muted/80" aria-hidden="true">
              <MapPin className={cn("text-muted-foreground/20", isHorizontal ? "h-10 w-10" : "h-14 w-14")} strokeWidth={1} />
            </div>
          )}

          {/* Category Badge - Glassmorphism style */}
          {!isHorizontal && experience.category && (
            <span className="absolute left-4 top-4 category-badge shadow-soft">
              {experience.category.icon && (
                <span aria-hidden="true" className="text-sm">{experience.category.icon}</span>
              )}
              <span className="font-medium">{experience.category.name}</span>
            </span>
          )}

          {/* Rating badge - Premium styling */}
          {settings.showRatings && experience.averageRating && experience.totalReviews > 0 && (
            <span
              className={cn(
                "absolute right-4 top-4",
                "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5",
                "glass text-sm font-semibold shadow-soft"
              )}
              aria-label={`Rated ${experience.averageRating.toFixed(1)} out of 5 stars based on ${experience.totalReviews} reviews`}
            >
              <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" aria-hidden="true" />
              <span className="font-bold">{experience.averageRating.toFixed(1)}</span>
              {isCurated && experience.totalReviews > 0 && (
                <span className="text-muted-foreground text-xs font-medium">({experience.totalReviews.toLocaleString()})</span>
              )}
            </span>
          )}

          {/* Curated source badge */}
          {isCurated && (
            <span className="absolute left-4 bottom-4 inline-flex items-center gap-1.5 rounded-full glass-subtle px-3 py-1.5 text-[11px] font-semibold text-foreground/80 shadow-soft">
              <ExternalLink className="h-3 w-3" />
              {sourceDisplayName}
            </span>
          )}

          {/* Price overlay on image - visible on hover (vertical only) */}
          {!isHorizontal && settings.showPrices && (
            <div className={cn(
              "absolute bottom-4 right-4",
              "opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0",
              "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
            )}>
              <span className="inline-flex items-baseline rounded-xl glass px-4 py-2 shadow-luxury">
                <span className="text-lg font-bold text-foreground">{priceText}</span>
                <span className="text-xs text-muted-foreground ml-1.5 font-medium">
                  /{experience.priceType === 'per_group' ? 'group' : 'person'}
                </span>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Content Section */}
      <div className={cn(
        "flex flex-1 flex-col",
        isHorizontal ? "justify-between p-5" : "p-5"
      )}>
        <div>
          {/* Category - horizontal or when no image */}
          {(isHorizontal || !settings.showImages) && experience.category && (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-elegant text-primary mb-2">
              {experience.category.icon && <span aria-hidden="true">{experience.category.icon}</span>}
              {experience.category.name}
            </span>
          )}

          {/* Curated badges */}
          <BadgeDisplay />

          {/* Title - Serif font for luxury feel */}
          <h3 className={cn(
            "font-serif leading-snug text-foreground tracking-luxury",
            "group-hover:text-primary transition-colors duration-500",
            isHorizontal ? "text-xl font-semibold" : "text-lg font-semibold"
          )}>
            {experience.name}
          </h3>

          {/* Description */}
          {experience.shortDescription && (
            <p className={cn(
              "mt-3 text-sm text-muted-foreground leading-relaxed",
              isHorizontal ? "line-clamp-2" : "line-clamp-2"
            )}>
              {experience.shortDescription}
            </p>
          )}

          {/* Concierge Note */}
          <ConciergeNote />
        </div>

        {/* Meta Info & Price */}
        <div className={cn(isHorizontal ? "mt-4" : "mt-4")}>
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 mb-4">
            {settings.showDuration && experience.durationMinutes && (
              <span className="meta-info" aria-label={`Duration: ${formatDuration(experience.durationMinutes)}`}>
                <span className="meta-info-icon">
                  <Clock strokeWidth={1.5} aria-hidden="true" />
                </span>
                <span className="font-medium text-foreground/80">{experience.durationDisplay || formatDuration(experience.durationMinutes)}</span>
              </span>
            )}
            {!isCurated && (
              <span className="meta-info" aria-label={`Up to ${experience.maxGuests} guests`}>
                <span className="meta-info-icon">
                  <Users strokeWidth={1.5} aria-hidden="true" />
                </span>
                <span className="font-medium text-foreground/80">{isHorizontal ? experience.maxGuests : `Up to ${experience.maxGuests}`}</span>
              </span>
            )}
          </div>

          {/* Price & CTA */}
          {settings.showPrices && (
            <div className={cn(
              "flex items-end justify-between pt-4",
              !isHorizontal && "border-t border-border/40"
            )}>
              <div className="flex flex-col">
                {!isHorizontal && (
                  <span className="text-[10px] font-medium uppercase tracking-elegant text-muted-foreground mb-0.5">
                    From
                  </span>
                )}
                {isCurated && (
                  <span className="text-[10px] text-muted-foreground/60 uppercase tracking-wide">
                    via {sourceDisplayName}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <span className="price-display text-foreground font-bold">
                    {priceText}
                  </span>
                  <span className="price-unit">
                    /{experience.priceType === 'per_group' ? 'group' : 'person'}
                  </span>
                </div>
                {isCurated ? (
                  <span className={cn(
                    "inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-xs font-semibold",
                    "bg-primary text-primary-foreground shadow-soft",
                    "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]",
                    "group-hover:shadow-luxury group-hover:-translate-y-0.5",
                    isRedirecting && "opacity-70 cursor-wait"
                  )}>
                    {isRedirecting ? 'Opening...' : 'Book'}
                    <ExternalLink className="h-3.5 w-3.5" />
                  </span>
                ) : (
                  <span className={cn(
                    "inline-flex items-center gap-1.5 rounded-xl px-5 py-2.5 text-xs font-semibold",
                    "bg-primary/10 text-primary",
                    "opacity-0 translate-x-2 group-hover:opacity-100 group-hover:translate-x-0",
                    "transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]"
                  )}>
                    View
                    <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );

  // Base card classes
  const baseCardClasses = cn(
    'group overflow-hidden rounded-2xl bg-card',
    'shadow-luxury',
    'border border-border/30',
    'transition-all duration-[600ms] ease-[cubic-bezier(0.22,1,0.36,1)]',
    'hover:shadow-luxury-hover hover:-translate-y-1.5',
    'hover:border-border/50',
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
    className
  );

  // Animation style for staggered loading
  const animationStyle = animationDelay > 0 ? {
    animationDelay: `${animationDelay}ms`,
    opacity: 0,
  } : {};

  // Horizontal layout
  if (layout === 'horizontal') {
    if (isCurated) {
      return (
        <button
          onClick={handleCuratedClick}
          disabled={isRedirecting}
          className={cn(baseCardClasses, 'flex text-left w-full', isRedirecting && 'cursor-wait', animationDelay > 0 && 'animate-slide-up')}
          style={animationStyle}
          aria-label={`${experience.name}. ${experience.shortDescription || ''}. ${priceLabel}. Opens ${sourceDisplayName} in new tab.`}
        >
          {cardContent(true)}
        </button>
      );
    }

    return (
      <Link
        to={internalLink}
        className={cn(baseCardClasses, 'flex', animationDelay > 0 && 'animate-slide-up')}
        style={animationStyle}
        aria-label={`${experience.name}. ${experience.shortDescription || ''}. ${priceLabel}`}
      >
        {cardContent(true)}
      </Link>
    );
  }

  // Vertical layout (default grid view)
  if (isCurated) {
    return (
      <button
        onClick={handleCuratedClick}
        disabled={isRedirecting}
        className={cn(baseCardClasses, 'flex flex-col text-left w-full', isRedirecting && 'cursor-wait', animationDelay > 0 && 'animate-slide-up')}
        style={animationStyle}
        aria-label={`${experience.name}. ${experience.shortDescription || ''}. ${priceLabel}. Opens ${sourceDisplayName} in new tab.`}
      >
        {cardContent(false)}
      </button>
    );
  }

  return (
    <Link
      to={internalLink}
      className={cn(baseCardClasses, 'flex flex-col', animationDelay > 0 && 'animate-slide-up')}
      style={animationStyle}
      aria-label={`${experience.name}. ${experience.shortDescription || ''}. ${priceLabel}`}
    >
      {cardContent(false)}
    </Link>
  );
}
